import React, { useCallback, useMemo, useState } from "react";
import type { CreditCardRecurring } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";
import { formatDateBR, formatMoneyBR } from "../../utils/format";
import { parseMoneyBR } from "../../utils/parseMoneyBr";
import { usePermission } from "../../auth/usePermission";

type RecurringItem = CreditCardRecurring & {
  card?: { id: number; name: string };
  account?: { id: number; code: string; name: string };
  costCenter?: { id: number; code?: string | null; name: string };
  category?: { id: number; name: string };
  supplier?: { id: number; entity?: { name: string } | null } | null;
};

const buildNextRunDate = (
  item: CreditCardRecurring,
  today = new Date()
) => {
  if (item.status !== "ACTIVE") {
    return null;
  }
  const billingDay = Math.min(28, Math.max(1, item.billingDay ?? new Date(item.startDate).getDate()));
  const base = new Date(today.getFullYear(), today.getMonth(), billingDay);
  const endDate = item.endDate ? new Date(item.endDate) : null;
  let next = base;
  if (base < today) {
    next = new Date(today.getFullYear(), today.getMonth() + 1, billingDay);
  }
  if (endDate && next > endDate) {
    return null;
  }
  return next;
};

export const CreditCardRecurringPage: React.FC = () => {
  const { client } = useAuth();
  const canRun = usePermission("FIN_CREDITCARD_RECURRING_RUN");
  const canCreate = usePermission("FIN_CREDITCARD_RECURRING_CREATE");
  const canEdit = usePermission("FIN_CREDITCARD_RECURRING_EDIT");
  const canDelete = usePermission("FIN_CREDITCARD_RECURRING_DELETE");

  const [cardFilter, setCardFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<"" | "ACTIVE" | "INACTIVE">("ACTIVE");
  const [cardOptions, setCardOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [runMonth, setRunMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [runCardId, setRunCardId] = useState<string>("");
  const [runResult, setRunResult] = useState<{
    created: number;
    skippedDuplicates: number;
    invoiceUpdated: number;
  } | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [runLoading, setRunLoading] = useState(false);
  const [historyFrom, setHistoryFrom] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-01-01`;
  });
  const [historyTo, setHistoryTo] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
      now.getDate()
    ).padStart(2, "0")}`;
  });
  const [historyCardId, setHistoryCardId] = useState<string>("");
  const [simulateBeforeGenerate, setSimulateBeforeGenerate] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyPreview, setHistoryPreview] = useState<{
    totalToCreate: number;
    byRecurring: Array<{
      recurringId: number;
      description: string;
      countToCreate: number;
      firstDate: string;
      lastDate: string;
    }>;
  } | null>(null);
  const [historyResult, setHistoryResult] = useState<{
    createdCount: number;
    skippedDuplicates: number;
    errorsCount: number;
  } | null>(null);

  const listFilters = useMemo(
    () => ({
      ...(cardFilter ? { cardId: cardFilter } : {}),
      ...(statusFilter === "ACTIVE" || statusFilter === "INACTIVE"
        ? { status: statusFilter }
        : {}),
    }),
    [cardFilter, statusFilter]
  );

  const fetchCards = useCallback(async (query: string) => {
    const response = await client.get(
      `/modules/cards?q=${encodeURIComponent(query)}&take=100&skip=0`
    );
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; name: string }>;
    };
    return payload.data.map((item) => ({ id: item.id, label: item.name }));
  }, [client]);

  React.useEffect(() => {
    const loadCards = async () => {
      const response = await client.get("/modules/cards?take=200&skip=0");
      if (!response.ok) {
        return;
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; name: string }>;
      };
      setCardOptions(payload.data);
    };
    void loadCards();
  }, [client]);

  const fetchAccounts = useCallback(async (query: string) => {
    const response = await client.get(
      `/modules/accounts?q=${encodeURIComponent(query)}&take=50&skip=0`
    );
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; code: string; name: string }>;
    };
    return payload.data.map((item) => ({
      id: item.id,
      label: `${item.code} - ${item.name}`,
    }));
  }, [client]);

  const fetchCostCenters = useCallback(async (query: string) => {
    const response = await client.get(
      `/modules/cost-centers?q=${encodeURIComponent(query)}&take=50&skip=0`
    );
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; code?: string | null; name: string }>;
    };
    return payload.data.map((item) => ({
      id: item.id,
      label: `${item.code ?? ""} ${item.name}`.trim(),
    }));
  }, [client]);

  const fetchCategories = useCallback(async (query: string) => {
    const response = await client.get(
      `/modules/categories?q=${encodeURIComponent(query)}&take=100&skip=0`
    );
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; name: string }>;
    };
    return payload.data.map((item) => ({ id: item.id, label: item.name }));
  }, [client]);

  const fetchSuppliers = useCallback(async (query: string) => {
    const response = await client.get(
      `/modules/suppliers?q=${encodeURIComponent(query)}&take=50&skip=0`
    );
    if (!response.ok) {
      return [];
    }
    const payload = (await response.json()) as {
      data: Array<{ id: number; entity: { name: string } }>;
    };
    return payload.data.map((item) => ({ id: item.id, label: item.entity.name }));
  }, [client]);

  const handleRun = useCallback(async () => {
    setRunError(null);
    setRunResult(null);
    setRunLoading(true);
    try {
      const [year, month] = runMonth.split("-").map(Number);
      const payload = {
        competenceYear: year,
        competenceMonth: month,
        ...(runCardId ? { cardId: Number(runCardId) } : {}),
      };
      const response = await client.post(
        "/modules/credit-cards/recurring/run",
        payload
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Falha ao gerar lancamentos.");
      }
      const data = (await response.json()) as {
        created: number;
        skippedDuplicates: number;
        invoiceUpdated: number;
      };
      setRunResult(data);
    } catch (err: unknown) {
      setRunError(err instanceof Error ? err.message : "Falha ao gerar lancamentos.");
    } finally {
      setRunLoading(false);
    }
  }, [client, runCardId, runMonth]);

  const historyPayload = useMemo(
    () => ({
      from: historyFrom,
      to: historyTo,
      ...(historyCardId ? { cardId: Number(historyCardId) } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
    }),
    [historyCardId, historyFrom, historyTo, statusFilter]
  );

  const handleHistoryPreview = useCallback(async () => {
    setHistoryError(null);
    setHistoryResult(null);
    setHistoryLoading(true);
    try {
      const response = await client.post(
        "/modules/credit-cards/recurring/generate-preview",
        historyPayload
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || body.error || "Falha ao simular geracao.");
      }
      const payload = (await response.json()) as {
        totalToCreate: number;
        byRecurring: Array<{
          recurringId: number;
          description: string;
          countToCreate: number;
          firstDate: string;
          lastDate: string;
        }>;
      };
      setHistoryPreview(payload);
    } catch (err: unknown) {
      setHistoryPreview(null);
      setHistoryError(err instanceof Error ? err.message : "Falha ao simular geracao.");
    } finally {
      setHistoryLoading(false);
    }
  }, [client, historyPayload]);

  const handleHistoryGenerate = useCallback(
    async (refresh: () => Promise<void>) => {
      setHistoryError(null);
      setHistoryLoading(true);
      try {
        if (simulateBeforeGenerate && !historyPreview) {
          await handleHistoryPreview();
          return;
        }
        const response = await client.post("/modules/credit-cards/recurring/generate", {
          ...historyPayload,
          mode: "RANGE",
          dryRun: false,
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.message || body.error || "Falha ao gerar historico.");
        }
        const payload = (await response.json()) as {
          createdCount: number;
          skippedDuplicates: number;
          errorsCount: number;
        };
        setHistoryResult(payload);
        await refresh();
      } catch (err: unknown) {
        setHistoryError(err instanceof Error ? err.message : "Falha ao gerar historico.");
      } finally {
        setHistoryLoading(false);
      }
    },
    [client, handleHistoryPreview, historyPayload, historyPreview, simulateBeforeGenerate]
  );

  const summaryLabel = useCallback((rows: RecurringItem[]) => {
    const total = rows
      .filter((item) => item.status === "ACTIVE")
      .reduce((sum, item) => sum + Number(item.monthlyAmount), 0);
    return formatMoneyBR(total);
  }, []);
  const cycleLabel = useMemo(() => {
    const now = new Date();
    return `${String(now.getMonth() + 1).padStart(2, "0")}/${now.getFullYear()}`;
  }, []);

  return (
    <>
      <CrudPage<RecurringItem>
        title="Recorrentes do cartao"
        endpoint="/modules/credit-cards/recurring"
        searchPlaceholder="Buscar por descricao..."
        permissionRead="FIN_CREDITCARD_RECURRING_VIEW"
        permissionCreate="FIN_CREDITCARD_RECURRING_CREATE"
        permissionEdit="FIN_CREDITCARD_RECURRING_EDIT"
        permissionDelete="FIN_CREDITCARD_RECURRING_DELETE"
        canWrite={canCreate || canEdit || canDelete}
        listFilters={listFilters}
        renderHeaderActions={({ rows, refresh }) => (
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="text-muted small">
              Total mensal previsto, {summaryLabel(rows)}
            </div>
            <div className="text-muted small">
              Proximo ciclo, {cycleLabel}
            </div>
            {canRun && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => setRunModalOpen(true)}
                >
                  Gerar lancamentos do mes
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setHistoryError(null);
                    setHistoryPreview(null);
                    setHistoryResult(null);
                    setHistoryModalOpen(true);
                  }}
                >
                  Gerar historico
                </button>
                {historyModalOpen && (
                  <>
                    <div className="modal fade show d-block" tabIndex={-1} role="dialog">
                      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content">
                          <div className="modal-header">
                            <h5 className="modal-title">Gerar historico de recorrencias</h5>
                            <button
                              type="button"
                              className="btn-close"
                              onClick={() => setHistoryModalOpen(false)}
                              disabled={historyLoading}
                            />
                          </div>
                          <div className="modal-body">
                            {historyError && <div className="alert alert-danger">{historyError}</div>}
                            {historyPreview && (
                              <div className="alert alert-info">
                                Simulacao, {historyPreview.totalToCreate} lancamentos podem ser criados.
                              </div>
                            )}
                            {historyResult && (
                              <div className="alert alert-success">
                                Criados, {historyResult.createdCount}. Duplicados ignorados,{" "}
                                {historyResult.skippedDuplicates}. Erros, {historyResult.errorsCount}.
                              </div>
                            )}
                            <div className="row g-3">
                              <div className="col-12 col-md-4">
                                <label className="form-label">De</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={historyFrom}
                                  onChange={(event) => setHistoryFrom(event.target.value)}
                                  disabled={historyLoading}
                                />
                              </div>
                              <div className="col-12 col-md-4">
                                <label className="form-label">Ate</label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={historyTo}
                                  onChange={(event) => setHistoryTo(event.target.value)}
                                  disabled={historyLoading}
                                />
                              </div>
                              <div className="col-12 col-md-4">
                                <label className="form-label">Cartao (opcional)</label>
                                <select
                                  className="form-select"
                                  value={historyCardId}
                                  onChange={(event) => setHistoryCardId(event.target.value)}
                                  disabled={historyLoading}
                                >
                                  <option value="">Todos</option>
                                  {cardOptions.map((card) => (
                                    <option key={card.id} value={String(card.id)}>
                                      {card.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <div className="form-check mt-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="simulate-history"
                                checked={simulateBeforeGenerate}
                                onChange={(event) => setSimulateBeforeGenerate(event.target.checked)}
                                disabled={historyLoading}
                              />
                              <label className="form-check-label" htmlFor="simulate-history">
                                Simular antes de gerar
                              </label>
                            </div>
                            {historyPreview && historyPreview.byRecurring.length > 0 && (
                              <div className="table-responsive mt-3">
                                <table className="table table-sm">
                                  <thead>
                                    <tr>
                                      <th>Recorrencia</th>
                                      <th>Qtd.</th>
                                      <th>Primeira</th>
                                      <th>Ultima</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {historyPreview.byRecurring.slice(0, 15).map((item) => (
                                      <tr key={item.recurringId}>
                                        <td>
                                          #{item.recurringId}, {item.description}
                                        </td>
                                        <td>{item.countToCreate}</td>
                                        <td>{formatDateBR(item.firstDate)}</td>
                                        <td>{formatDateBR(item.lastDate)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            <small className="text-muted d-block mt-2">
                              Limite de seguranca, maximo de 36 meses por execucao.
                            </small>
                          </div>
                          <div className="modal-footer">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setHistoryModalOpen(false)}
                              disabled={historyLoading}
                            >
                              Fechar
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={handleHistoryPreview}
                              disabled={historyLoading}
                            >
                              {historyLoading ? "Processando..." : "Simular"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => {
                                void handleHistoryGenerate(refresh);
                              }}
                              disabled={historyLoading || (simulateBeforeGenerate && !historyPreview)}
                            >
                              {historyLoading ? "Processando..." : "Confirmar geracao"}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="modal-backdrop fade show" />
                  </>
                )}
              </>
            )}
          </div>
        )}
        renderFilters={
          <div className="row g-2">
            <div className="col-12 col-md-4">
              <label className="form-label mb-1">Cartao</label>
              <select
                className="form-select"
                value={cardFilter}
                onChange={(event) => setCardFilter(event.target.value)}
              >
                <option value="">Todos</option>
                {cardOptions.map((card) => (
                  <option key={card.id} value={card.id}>
                    {card.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label mb-1">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(event.target.value as "" | "ACTIVE" | "INACTIVE")
                }
              >
                <option value="">Todos</option>
                <option value="ACTIVE">Ativo</option>
                <option value="INACTIVE">Inativo</option>
              </select>
            </div>
          </div>
        }
        columns={[
          { label: "Descricao", render: (item) => item.description },
          { label: "Cartao", render: (item) => item.card?.name ?? "-" },
          {
            label: "Valor mensal",
            render: (item) => formatMoneyBR(Number(item.monthlyAmount)),
          },
          { label: "Inicio", render: (item) => formatDateBR(item.startDate) },
          { label: "Termino", render: (item) => item.endDate ? formatDateBR(item.endDate) : "-" },
          {
            label: "Proximo lancamento",
            render: (item) => {
              const next = buildNextRunDate(item);
              return next ? formatDateBR(next.toISOString()) : "-";
            },
          },
          { label: "Status", render: (item) => item.status },
        ]}
        formFields={[
          {
            name: "cardId",
            label: "Cartao",
            type: "select",
            required: true,
            fetchOptions: fetchCards,
          },
          {
            name: "accountId",
            label: "Conta contabil",
            type: "autocomplete",
            placeholder: "Buscar conta contabil",
            fetchOptions: fetchAccounts,
            getIdFromItem: (item) => item.accountId ?? null,
            getLabelFromItem: (item) =>
              item.account ? `${item.account.code} - ${item.account.name}` : "",
          },
          {
            name: "costCenterId",
            label: "Centro de custo",
            type: "select",
            required: true,
            fetchOptions: fetchCostCenters,
          },
          {
            name: "categoryId",
            label: "Categoria",
            type: "select",
            required: true,
            fetchOptions: fetchCategories,
          },
          {
            name: "supplierId",
            label: "Fornecedor",
            type: "select",
            fetchOptions: fetchSuppliers,
          },
          { name: "description", label: "Descricao", type: "text", required: true },
          {
            name: "monthlyAmount",
            label: "Valor mensal",
            type: "text",
            required: true,
            displayValue: (value) => formatMoneyBR(parseMoneyBR(value) ?? 0),
          },
          { name: "startDate", label: "Data de inicio", type: "date", required: true },
          { name: "endDate", label: "Data de termino", type: "date" },
          {
            name: "billingDay",
            label: "Dia de cobranca (1-28)",
            type: "number",
          },
          {
            name: "status",
            label: "Status",
            type: "select",
            options: [
              { value: "ACTIVE", label: "Ativo" },
              { value: "PAUSED", label: "Pausado" },
              { value: "ENDED", label: "Encerrado" },
            ],
          },
        ]}
        initialValues={{
          cardId: null,
          accountId: null,
          costCenterId: null,
          categoryId: null,
          supplierId: null,
          description: "",
          monthlyAmount: "",
          startDate: "",
          endDate: "",
          billingDay: null,
          status: "ACTIVE",
        }}
        mapToPayload={(values) => {
          const monthlyAmount = parseMoneyBR(values.monthlyAmount);
          if (monthlyAmount === null) {
            throw new Error("Valor mensal invalido.");
          }
          return {
            cardId: values.cardId ? Number(values.cardId) : null,
            accountId: values.accountId ? Number(values.accountId) : null,
            costCenterId: values.costCenterId ? Number(values.costCenterId) : null,
            categoryId: values.categoryId ? Number(values.categoryId) : null,
            supplierId: values.supplierId ? Number(values.supplierId) : null,
            description: values.description,
            monthlyAmount,
            startDate: values.startDate,
            endDate: values.endDate || null,
            billingDay: values.billingDay ? Number(values.billingDay) : null,
            status: values.status || "ACTIVE",
          };
        }}
        customActions={(_, __) => null}
      />

      {runModalOpen && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Gerar lancamentos do mes</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setRunModalOpen(false)}
                    disabled={runLoading}
                  />
                </div>
                <div className="modal-body">
                  {runError && <div className="alert alert-danger">{runError}</div>}
                  {runResult && (
                    <div className="alert alert-success">
                      Criados, {runResult.created}. Duplicados, {runResult.skippedDuplicates}.
                      Faturas atualizadas, {runResult.invoiceUpdated}.
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">Competencia</label>
                    <input
                      type="month"
                      className="form-control"
                      value={runMonth}
                      onChange={(event) => setRunMonth(event.target.value)}
                      disabled={runLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Cartao (opcional)</label>
                    <select
                      className="form-select"
                      value={runCardId}
                      onChange={(event) => setRunCardId(event.target.value)}
                      disabled={runLoading}
                    >
                      <option value="">Todos</option>
                      {cardOptions.map((card) => (
                        <option key={card.id} value={String(card.id)}>
                          {card.name}
                        </option>
                      ))}
                    </select>
                    <small className="text-muted">
                      Deixe vazio para rodar todos.
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setRunModalOpen(false)}
                    disabled={runLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleRun}
                    disabled={runLoading}
                  >
                    {runLoading ? "Processando..." : "Gerar"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};
